/**
 * Admin controls: teams, players, squares, axes, scoring, join requests.
 *
 * Every button here dispatches a named command to the Durable Object, which
 * re-checks that the caller is the game's admin. The original gated this panel
 * behind a shared password fetched from Firestore and compared in the browser,
 * so anyone could open the console and call the same functions directly.
 */

import { useState } from 'react';
import {
  amountOwed,
  currentQuarter,
  paymentSummary,
  totalPot,
  lastLockedQuarter,
  parseAxisInput,
  potRemaining,
  squaresAvailable,
} from '../../shared/game-logic';
import {
  allowsEveryScore,
  leagueMeta,
  periodLabel,
  scoresCovered,
} from '../../shared/sports';
import type { ClientCommand } from '../../shared/protocol';
import type { GameState, TeamKey } from '../../shared/types';
import { TeamPicker } from './TeamPicker';
import { formatMoney } from '../lib/format';

interface Props {
  state: GameState;
  send: (command: ClientCommand) => void;
}


export function AdminPanel({ state, send }: Props) {
  const everyScoreOffered = allowsEveryScore(state.config.league);
  const [pickingTeam, setPickingTeam] = useState<TeamKey | null>(null);
  const available = squaresAvailable(state);
  const q = currentQuarter(state);
  const pending = state.joinRequests.filter((r) => r.status === 'pending');

  return (
    <div className="stack">
      {pending.length > 0 && (
        <section className="card">
          <h2 className="card-title">Pending requests ({pending.length})</h2>
          <div className="stack">
            {pending.map((request) => (
              <RequestRow
                key={request.id}
                name={request.name}
                requested={request.count}
                max={available}
                onApprove={(count) =>
                  send({ type: 'request.approve', requestId: request.id, count })
                }
                onReject={() => send({ type: 'request.reject', requestId: request.id })}
              />
            ))}
          </div>
        </section>
      )}

      <section className="card">
        <h2 className="card-title">Matchup</h2>
        <div className="field-row">
          {(['home', 'away'] as TeamKey[]).map((side) => (
            <div key={side}>
              <label htmlFor={`team-${side}`}>{side === 'home' ? 'Home' : 'Away'}</label>
              <input
                id={`team-${side}`}
                defaultValue={state.config.teams[side].name}
                onBlur={(e) =>
                  send({ type: 'team.update', team: side, patch: { name: e.target.value } })
                }
              />
              <div className="row" style={{ marginTop: 8 }}>
                <input
                  type="color"
                  aria-label={`${side} team color`}
                  style={{ width: 60 }}
                  defaultValue={state.config.teams[side].color}
                  onChange={(e) =>
                    send({ type: 'team.update', team: side, patch: { color: e.target.value } })
                  }
                />
                <button type="button" onClick={() => setPickingTeam(side)}>
                  Choose team…
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h2 className="card-title">Game setup</h2>
        <div className="field-row">
          <div className="field">
            <label htmlFor="cfg-title">Title</label>
            <input
              id="cfg-title"
              defaultValue={state.config.title}
              onBlur={(e) => send({ type: 'config.update', patch: { title: e.target.value } })}
            />
          </div>
          <div className="field">
            <label htmlFor="cfg-cost">Cost per square</label>
            <input
              id="cfg-cost"
              type="number"
              min={0.01}
              step={0.01}
              defaultValue={state.config.squareCost}
              onBlur={(e) =>
                send({
                  type: 'config.update',
                  patch: { squareCost: Number(e.target.value) },
                })
              }
            />
          </div>
        </div>

        {/* Mode and the deadline each take a full line.
            `datetime-local` renders as a fixed-width control on iOS -- it will
            not shrink to a grid column, so in a two-up row on a phone the date
            simply ran off the edge of the card. */}
        <div className="stack" style={{ gap: 12, marginTop: 12 }}>
          <div className="field">
            <label htmlFor="cfg-mode">Mode</label>
            <select
              id="cfg-mode"
              value={state.config.mode}
              onChange={(e) =>
                send({
                  type: 'config.update',
                  patch: { mode: e.target.value as 'standard' | 'everyscore' },
                })
              }
            >
              <option value="standard">
                Standard (per {leagueMeta(state.config.league).periodNoun})
              </option>
              {/* Offered only where a game has few enough scoring moments for
                  someone to log each one. See allowsEveryScore. */}
              {everyScoreOffered && <option value="everyscore">Every score</option>}
            </select>
            {!everyScoreOffered && (
              <span className="small muted">
                Every-score pools are football only.{' '}
                {leagueMeta(state.config.league).label} games change the score about{' '}
                {leagueMeta(state.config.league).expectedScoreChanges} times.
              </span>
            )}
          </div>
          <div className="field">
            <label htmlFor="cfg-deadline">Request deadline</label>
            <input
              id="cfg-deadline"
              type="datetime-local"
              defaultValue={toLocalInput(state.config.requestDeadline)}
              onBlur={(e) =>
                send({
                  type: 'config.update',
                  patch: {
                    requestDeadline: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : '',
                  },
                })
              }
            />
          </div>
        </div>

        <p className="small muted">
          Total pot {formatMoney(state.config.squareCost * 100)} · {available} squares
          unsold
          {state.config.mode === 'everyscore' &&
            ` · ${formatMoney(potRemaining(state))} unpaid`}
        </p>
      </section>

      <PayoutSettings state={state} send={send} />
      <PlayerManager state={state} send={send} available={available} />
      <AxisControls state={state} send={send} />

      <section className="card">
        <h2 className="card-title">Scoring</h2>
        <LiveScoring state={state} send={send} />
        <ScoreControls state={state} send={send} quarter={q} />
      </section>

      {pickingTeam && (
        <TeamPicker
          side={pickingTeam}
          onClose={() => setPickingTeam(null)}
          onSelect={(team) => {
            send({
              type: 'team.update',
              team: pickingTeam,
              patch: {
                name: team.name,
                abbr: team.abbr,
                conference: team.conference,
                league: team.league,
                logoUrl: team.logoUrl,
                ...(team.primaryColor ? { color: team.primaryColor } : {}),
              },
            });
            setPickingTeam(null);
          }}
        />
      )}
    </div>
  );
}

function RequestRow({
  name,
  requested,
  max,
  onApprove,
  onReject,
}: {
  name: string;
  requested: number;
  max: number;
  onApprove: (count: number) => void;
  onReject: () => void;
}) {
  const [count, setCount] = useState(Math.min(requested, max));
  return (
    <div className="row">
      <strong style={{ minWidth: 120 }}>{name}</strong>
      <span className="small muted">asked for {requested}</span>
      <div className="spacer" />
      <input
        type="number"
        min={1}
        max={max}
        value={count}
        aria-label={`Squares to approve for ${name}`}
        style={{ width: 90 }}
        onChange={(e) => setCount(Number(e.target.value))}
      />
      <button
        type="button"
        className="btn-primary"
        disabled={count < 1 || count > max}
        onClick={() => onApprove(count)}
      >
        Approve
      </button>
      <button type="button" className="btn-danger" onClick={onReject}>
        Reject
      </button>
    </div>
  );
}

function PayoutSettings({ state, send }: Props) {
  if (state.config.mode === 'everyscore') {
    const es = state.config.payouts.everyscore;
    const covered = scoresCovered(totalPot(state), es.perScore);
    const expectedChanges = leagueMeta(state.config.league).expectedScoreChanges;
    return (
      <section className="card">
        <h2 className="card-title">Payouts</h2>
        <div className="field-row">
          <div className="field">
            <label htmlFor="per-score">Per score</label>
            <input
              id="per-score"
              type="number"
              min={0}
              defaultValue={es.perScore}
              onBlur={(e) =>
                send({
                  type: 'config.update',
                  patch: { payouts: { everyscore: { perScore: Number(e.target.value) } } },
                })
              }
            />
          </div>
        </div>
        {/* The figure alone says nothing useful. What matters is whether it
            lasts the game, and that depends entirely on the sport: an NFL
            game changes its score eight to fourteen times, an NBA game well
            over a hundred. */}
        <p className="small muted" style={{ marginTop: 4 }}>
          Covers {covered} score{covered === 1 ? '' : 's'} of the{' '}
          {formatMoney(totalPot(state))} pot. A typical {leagueMeta(state.config.league).label}{' '}
          game changes the score about {expectedChanges} times.
          {covered < expectedChanges && (
            <strong style={{ color: 'var(--warn)', display: 'block', marginTop: 2 }}>
              The pot runs out before the game does — scores after the{' '}
              {covered}
              {ordinalSuffix(covered)} pay nothing, and the final twist gets nothing.
            </strong>
          )}
        </p>
        <label className="row" style={{ textTransform: 'none', letterSpacing: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', minHeight: 'auto' }}
            checked={es.finalTwist}
            onChange={(e) =>
              send({
                type: 'config.update',
                patch: { payouts: { everyscore: { finalTwist: e.target.checked } } },
              })
            }
          />
          <span>Award the final twist (75/25 split of the unpaid pot)</span>
        </label>
      </section>
    );
  }

  const payingOut = state.config.payouts.standard.reduce((sum, n) => sum + n, 0);
  const collected = totalPot(state);

  return (
    <section className="card">
      <h2 className="card-title">Quarter payouts</h2>
      {/* A warning, not a block: an admin may be topping the pot up from
          elsewhere, or deliberately not paying all of it out. Finding out at
          settlement time is the bad outcome. */}
      <p
        className="small"
        style={{
          marginBottom: 12,
          color: payingOut > collected ? 'var(--warn)' : 'var(--text-dim)',
        }}
      >
        Paying out {formatMoney(payingOut)} of {formatMoney(collected)} collected
        {payingOut > collected &&
          ` · ${formatMoney(payingOut - collected)} more than the pot holds`}
        {payingOut < collected &&
          ` · ${formatMoney(collected - payingOut)} left over`}
      </p>
      <div className="field-row">
        {state.config.payouts.standard.map((amount, i) => (
          <div className="field" key={i}>
            <label htmlFor={`payout-${i}`}>{periodLabel(state.config.league, i)}</label>
            <input
              id={`payout-${i}`}
              type="number"
              min={0}
              defaultValue={amount}
              onBlur={(e) => {
                const next = [...state.config.payouts.standard];
                next[i] = Number(e.target.value);
                send({ type: 'config.update', patch: { payouts: { standard: next } } });
              }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function PlayerManager({
  state,
  send,
  available,
}: Props & { available: number }) {
  const [name, setName] = useState('');
  const [count, setCount] = useState('1');

  const add = () => {
    const n = name.trim();
    const c = Number(count);
    if (!n || !Number.isInteger(c) || c < 1) return;
    send({ type: 'player.add', name: n, count: c });
    setName('');
    setCount('1');
  };

  return (
    <section className="card">
      <h2 className="card-title">Players</h2>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <input
          placeholder="Player name"
          aria-label="Player name"
          value={name}
          style={{ flex: '2 1 160px' }}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="number"
          min={1}
          max={available}
          aria-label="Squares"
          value={count}
          style={{ flex: '0 1 90px' }}
          onChange={(e) => setCount(e.target.value)}
        />
        <button type="submit" className="btn-primary" disabled={!name.trim()}>
          Add
        </button>
      </form>

      {state.players.length > 0 && (
        <div className="stack" style={{ marginTop: 12, gap: 8 }}>
          {state.players.map((player) => (
            <div className="row" key={player.id}>
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: player.color,
                  flexShrink: 0,
                }}
              />
              <strong>{player.name}</strong>
              <div className="spacer" />
              <span className="small muted mono" style={{ whiteSpace: 'nowrap' }}>
                {formatMoney(amountOwed(state, player))}
              </span>
              <button
                type="button"
                className={player.paidAt ? 'paid-toggle is-paid' : 'paid-toggle'}
                aria-pressed={Boolean(player.paidAt)}
                title={player.paidAt ? 'Paid \u2014 tap to undo' : 'Mark as paid'}
                onClick={() =>
                  send({
                    type: 'player.setPaid',
                    playerId: player.id,
                    paid: !player.paidAt,
                  })
                }
              >
                {player.paidAt ? '\u2713 Paid' : 'Unpaid'}
              </button>
              <input
                type="number"
                min={0}
                value={player.count}
                aria-label={`Squares for ${player.name}`}
                style={{ width: 78 }}
                onChange={(e) =>
                  send({
                    type: 'player.updateCount',
                    playerId: player.id,
                    count: Number(e.target.value),
                  })
                }
              />
              <button
                type="button"
                className="btn-danger"
                onClick={() => {
                  if (confirm(`Remove ${player.name} and free their squares?`)) {
                    send({ type: 'player.remove', playerId: player.id });
                  }
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {state.players.length > 0 && <PaymentTotals state={state} />}

      <div className="row" style={{ marginTop: 12 }}>
        <button type="button" className="btn-primary" onClick={() => send({ type: 'squares.assign' })}>
          Assign squares randomly
        </button>
        <button
          type="button"
          className="btn-danger"
          onClick={() => {
            if (confirm('Clear every square on the board?')) send({ type: 'squares.clear' });
          }}
        >
          Clear board
        </button>
      </div>
    </section>
  );
}

/** Who has settled up. The admin is the one chasing this, so it lives here. */
function PaymentTotals({ state }: { state: GameState }) {
  const { collected, outstanding, paidCount, unpaidCount } = paymentSummary(state);
  return (
    <div className="payment-totals">
      <span>
        <strong className="mono">{formatMoney(collected)}</strong>
        <span className="small muted"> collected from {paidCount}</span>
      </span>
      {outstanding > 0 && (
        <span className="outstanding">
          <strong className="mono">{formatMoney(outstanding)}</strong>
          <span className="small"> owed by {unpaidCount}</span>
        </span>
      )}
    </div>
  );
}

function AxisControls({ state, send }: Props) {
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [confirmingReveal, setConfirmingReveal] = useState(false);
  const unpaid = paymentSummary(state).unpaidCount;
  const valid = parseAxisInput(home) !== null && parseAxisInput(away) !== null;

  return (
    <section className="card">
      <h2 className="card-title">Axis numbers</h2>
      {state.axesRevealed ? (
        <p className="small muted">
          Revealed. {state.config.teams.home.abbr}: {state.axes.home.join(' ')} ·{' '}
          {state.config.teams.away.abbr}: {state.axes.away.join(' ')}
        </p>
      ) : (
        <>
          <p className="small muted" style={{ marginBottom: 12 }}>
            Digits stay hidden from players until you reveal them. The draw runs on
            the server using cryptographic randomness.
          </p>
          {confirmingReveal ? (
            <div className="reveal-confirm" role="group" aria-label="Confirm reveal">
              <strong style={{ display: 'block', marginBottom: 6 }}>
                This cannot be undone.
              </strong>
              <p className="small" style={{ marginBottom: 10 }}>
                Drawing the numbers locks the board for good. Nobody can be added
                or removed, squares cannot be reassigned, and there is no way back
                &mdash; if you do this too early your only option is to delete the
                game and start over.
              </p>
              <p className="small muted" style={{ marginBottom: 12 }}>
                {squaresAvailable(state) > 0
                  ? `${squaresAvailable(state)} squares are still unsold. They will stay empty and win nothing.`
                  : 'All 100 squares are sold.'}
                {unpaid > 0 && ` ${unpaid} player${unpaid === 1 ? ' has' : 's have'} not paid yet.`}
              </p>
              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => {
                    setConfirmingReveal(false);
                    send({ type: 'axes.reveal' });
                  }}
                >
                  Draw the numbers
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setConfirmingReveal(false)}
                >
                  Not yet
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setConfirmingReveal(true)}
            >
              Draw &amp; reveal numbers
            </button>
          )}

          <details style={{ marginTop: 16 }}>
            <summary className="small muted" style={{ cursor: 'pointer' }}>
              Enter numbers manually (drawn offline)
            </summary>
            <div className="field-row" style={{ marginTop: 12 }}>
              <div className="field">
                <label htmlFor="axis-home">{state.config.teams.home.abbr} digits</label>
                <input
                  id="axis-home"
                  placeholder="3 7 1 0 9 5 2 8 4 6"
                  value={home}
                  onChange={(e) => setHome(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="axis-away">{state.config.teams.away.abbr} digits</label>
                <input
                  id="axis-away"
                  placeholder="0 4 8 2 6 1 9 3 7 5"
                  value={away}
                  onChange={(e) => setAway(e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              disabled={!valid}
              onClick={() =>
                send({
                  type: 'axes.setManual',
                  home: parseAxisInput(home)!,
                  away: parseAxisInput(away)!,
                })
              }
            >
              Apply
            </button>
          </details>
        </>
      )}
    </section>
  );
}

/**
 * Live score feed controls.
 *
 * Only the running score is filled in automatically. Ending a quarter stays a
 * deliberate press, so a feed glitch or a score corrected after review can
 * never pay out the wrong square.
 */
function LiveScoring({ state, send }: Props) {
  const source = state.config.source;
  const live = state.live;

  if (!source) {
    return (
      <p className="small muted" style={{ marginBottom: 14 }}>
        Live scores are available for games picked from a schedule. This one was
        set up by hand, so enter scores below.
      </p>
    );
  }

  if (!live) {
    return (
      <div className="live-bar" style={{ marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ display: 'block' }}>Live scores are off</strong>
          <span className="small muted">
            Follow the real game and fill the score in automatically. You still
            end each quarter yourself.
          </span>
        </div>
        <button type="button" className="btn-primary" onClick={() => send({ type: 'live.start' })}>
          Start
        </button>
      </div>
    );
  }

  return (
    <div className="live-bar live-on" style={{ marginBottom: 14 }}>
      <span className="live-dot" aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ display: 'block' }}>{liveHeadline(live)}</strong>
        <span className="small muted">
          {live.error
            ? live.error
            : live.lastUpdateAt
              ? `Updated ${timeAgo(live.lastUpdateAt)}`
              : 'Waiting for the first update…'}
        </span>
      </div>
      <button type="button" onClick={() => send({ type: 'live.stop' })}>
        Stop
      </button>
    </div>
  );
}

function liveHeadline(live: NonNullable<GameState['live']>): string {
  if (live.state === 'pre') return 'Live scores on · waiting for kickoff';
  if (live.state === 'post') return 'Live scores on · game finished';
  const period = live.period >= 5 ? 'OT' : `Q${live.period}`;
  return `Live · ${period}${live.displayClock ? ' ' + live.displayClock : ''}`;
}

function timeAgo(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.round(seconds / 60)}m ago`;
}

function ScoreControls({
  state,
  send,
  quarter,
}: Props & { quarter: ReturnType<typeof currentQuarter> }) {
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const lastLocked = lastLockedQuarter(state);
  const feedOn = state.live !== null;

  // While the feed is on it owns the score boxes; typing over a value that is
  // about to be overwritten would just be confusing.
  const shownHome = feedOn ? String(state.liveScore.home ?? '') : home;
  const shownAway = feedOn ? String(state.liveScore.away ?? '') : away;

  const parsed = feedOn
    ? { home: state.liveScore.home, away: state.liveScore.away }
    : { home: toScore(home), away: toScore(away) };
  const complete = parsed.home !== null && parsed.away !== null;

  return (
    <>
      <div className="field-row">
        <div className="field">
          <label htmlFor="score-home">{state.config.teams.home.abbr}</label>
          <input
            id="score-home"
            type="number"
            min={0}
            inputMode="numeric"
            value={shownHome}
            readOnly={feedOn}
            onChange={(e) => {
              if (feedOn) return;
              setHome(e.target.value);
              send({ type: 'score.setLive', home: toScore(e.target.value), away: parsed.away });
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="score-away">{state.config.teams.away.abbr}</label>
          <input
            id="score-away"
            type="number"
            min={0}
            inputMode="numeric"
            value={shownAway}
            readOnly={feedOn}
            onChange={(e) => {
              if (feedOn) return;
              setAway(e.target.value);
              send({ type: 'score.setLive', home: parsed.home, away: toScore(e.target.value) });
            }}
          />
        </div>
      </div>

      <div className="row">
        {state.config.mode === 'everyscore' && (
          <button
            type="button"
            className="btn-primary"
            disabled={!complete}
            onClick={() => {
              send({ type: 'score.log' });
              setHome('');
              setAway('');
            }}
          >
            Log score
          </button>
        )}
        <button
          type="button"
          className="btn-primary"
          disabled={!complete || quarter === null}
          onClick={() => {
            send({ type: 'quarter.end', home: parsed.home!, away: parsed.away! });
            setHome('');
            setAway('');
          }}
        >
          End {quarter === null ? 'game' : periodLabel(state.config.league, quarter)}
        </button>
        <button
          type="button"
          disabled={lastLocked === null}
          onClick={() => {
            if (
              lastLocked !== null &&
              confirm(`Reopen ${periodLabel(state.config.league, lastLocked)}?`)
            ) {
              send({ type: 'quarter.reopen' });
            }
          }}
        >
          Reopen last quarter
        </button>
      </div>

      {state.config.mode === 'everyscore' && state.scoreLog.length > 0 && (
        <div className="stack" style={{ marginTop: 16, gap: 6 }}>
          <h3 className="card-title">Score log</h3>
          {state.scoreLog.map((entry) => (
            <div className="row small" key={entry.id}>
              <span className="mono">
                {state.config.teams.home.abbr} {entry.home} – {state.config.teams.away.abbr}{' '}
                {entry.away}
              </span>
              {!entry.paid && <span className="badge">unpaid</span>}
              <div className="spacer" />
              <button
                type="button"
                className="btn-ghost"
                onClick={() => send({ type: 'scoreLog.delete', scoreId: entry.id })}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function toScore(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 999 ? n : null;
}

/** ISO string -> the `datetime-local` input format, in the viewer's zone. */
function toLocalInput(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** "st", "nd", "rd", "th" -- for "scores after the 5th pay nothing". */
function ordinalSuffix(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}
